import React, { useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Loader2,
  Mail,
  RefreshCw,
  Users,
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';

const statusBadgeClass = (status) => {
  switch (status) {
    case 'sent':
      return 'bg-green-500/10 text-green-400 border-green-500/20';
    case 'failed':
      return 'bg-red-500/10 text-red-400 border-red-500/20';
    case 'skipped':
      return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
    default:
      return 'bg-slate-500/10 text-slate-300 border-slate-500/20';
  }
};

const formatMoney = (amount) => {
  const numeric = Number(amount || 0);
  return `$${numeric.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const SupportersTab = ({
  overview,
  loading,
  resyncingIds,
  onRefresh,
  onResync,
}) => {
  const [supportersOpen, setSupportersOpen] = useState(true);
  const [syncEventsOpen, setSyncEventsOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-aqua animate-spin" />
      </div>
    );
  }

  const stats = overview?.stats || {};
  const supporters = overview?.supporters || [];
  const resendConfig = overview?.resend_config || {};
  const recentSyncEvents = overview?.recent_sync_events || [];
  const configRows = [
    ['Resend API key', resendConfig.has_resend_api_key],
    ['General support segment', resendConfig.has_support_segment_id],
    ['Monthly supporter segment', resendConfig.has_monthly_segment_id],
    ['Support updates topic', resendConfig.has_updates_topic_id],
    ['Monthly support topic', resendConfig.has_monthly_topic_id],
  ];

  return (
    <div className="space-y-6">
      <Card className="border-emerald-200 bg-gradient-to-br from-[#eef8f2] via-white to-[#edf4f1] p-6 dark:border-emerald-500/15 dark:bg-gradient-to-br dark:from-[#162019] dark:via-[#121814] dark:to-[#101512]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-emerald-700 dark:text-emerald-300">
          Business Health flow
        </p>
        <h3 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
          Keep mission support visible, synced, and trustworthy
        </h3>
        <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
          This tool is for supporter records, sync health, and the operational trust that sits behind mission support. When something here drifts, it creates confusion quickly.
        </p>
      </Card>

      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Mission Support</h2>
          <p className="text-sm text-gray-400 mt-1">
            Review supporters, Resend sync health, and manual resync actions.
          </p>
        </div>
        <Button onClick={onRefresh} variant="outline" className="border-[#2a2a32] text-white hover:bg-[#16161a]">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
        {[
          ['Failed syncs', stats.failed_syncs || 0, AlertCircle],
          ['Total supporters', stats.total_supporters || 0, Users],
          ['Monthly supporters', stats.monthly_supporters || 0, Mail],
          ['One-time supporters', stats.one_time_supporters || 0, Mail],
          ['Subscriber only', stats.subscriber_only || 0, Mail],
          ['Lifetime support', formatMoney(stats.lifetime_support_total), DollarSign],
        ].map(([label, value, Icon]) => (
          <Card key={label} className="bg-[#1e1e24] border-[#2a2a32] p-5">
            <div className="flex items-start justify-between mb-3">
              <Icon className="w-6 h-6 text-aqua" />
            </div>
            <p className="text-gray-400 text-sm">{label}</p>
            <span className="text-2xl font-bold text-white">{value}</span>
          </Card>
        ))}
      </div>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-aqua" />
          Resend wiring status
        </h3>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {configRows.map(([label, ready]) => (
            <div key={label} className="rounded-xl bg-[#16161a] p-4">
              <p className="text-gray-400 text-sm">{label}</p>
              <Badge className={`mt-3 ${ready ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'}`}>
                {ready ? 'Configured' : 'Missing'}
              </Badge>
            </div>
          ))}
        </div>
      </Card>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <button
          type="button"
          onClick={() => setSupportersOpen((o) => !o)}
          className="flex w-full items-center justify-between mb-4 text-left"
        >
          <h3 className="text-lg font-semibold text-white">Supporters</h3>
          {supportersOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {supportersOpen && (supporters.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No supporters recorded yet</p>
        ) : (
          <div className="space-y-4">
            {supporters.map((supporter) => {
              const isResyncing = resyncingIds.includes(supporter.supporter_id);
              return (
                <div key={supporter.supporter_id} className="rounded-xl bg-[#16161a] p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-white font-medium">{supporter.name || 'Supporter'}</p>
                          <Badge className="bg-aqua/10 text-aqua border-aqua/20">{supporter.support_status || 'unknown'}</Badge>
                          {!supporter.consent_updates && (
                            <Badge className="bg-slate-500/10 text-slate-300 border-slate-500/20">Unsubscribed</Badge>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm mt-1">{supporter.email}</p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Badge className={statusBadgeClass(supporter.resend_sync_status)}>{`Resend ${supporter.resend_sync_status || 'unknown'}`}</Badge>
                        <Badge className={statusBadgeClass(supporter.resend_contact_status)}>{`Contact ${supporter.resend_contact_status || 'unknown'}`}</Badge>
                        <Badge className={statusBadgeClass(supporter.resend_topic_status)}>{`Topics ${supporter.resend_topic_status || 'unknown'}`}</Badge>
                        <Badge className={statusBadgeClass(supporter.resend_segment_status)}>{`Segments ${supporter.resend_segment_status || 'unknown'}`}</Badge>
                      </div>

                      <div className="grid gap-2 text-sm text-gray-400 md:grid-cols-2 xl:grid-cols-4">
                        <p>{`Audience: ${supporter.audience_tag || '—'}`}</p>
                        <p>{`Support count: ${supporter.support_count || 0}`}</p>
                        <p>{`Lifetime: ${formatMoney((supporter.lifetime_support_cents || 0) / 100)}`}</p>
                        <p>{`Last support: ${formatDate(supporter.last_support_at)}`}</p>
                      </div>

                      {supporter.resend_sync_detail ? (
                        <p className="text-xs text-gray-500">{supporter.resend_sync_detail}</p>
                      ) : null}
                    </div>

                    <div className="flex flex-col items-start gap-3 lg:items-end">
                      <Button
                        onClick={() => onResync(supporter.supporter_id)}
                        disabled={isResyncing}
                        variant="outline"
                        className="border-[#2a2a32] text-white hover:bg-[#11161d]"
                      >
                        {isResyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        Resync
                      </Button>
                      <div className="text-right text-xs text-gray-500">
                        <p>{`Last sync: ${formatDate(supporter.last_support_synced_at)}`}</p>
                        <p>{`Latest event: ${supporter.latest_sync?.event_type || '—'}`}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </Card>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <button
          type="button"
          onClick={() => setSyncEventsOpen((o) => !o)}
          className="flex w-full items-center justify-between mb-4 text-left"
        >
          <h3 className="text-lg font-semibold text-white">Recent sync events</h3>
          {syncEventsOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {syncEventsOpen && (recentSyncEvents.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No sync events recorded yet</p>
        ) : (
          <div className="space-y-3">
            {recentSyncEvents.map((event) => (
              <div key={event.sync_id} className="rounded-xl bg-[#16161a] p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={statusBadgeClass(event.status)}>{event.status}</Badge>
                  <p className="text-white text-sm font-medium">{event.event_type}</p>
                  <p className="text-gray-500 text-xs">{formatDate(event.created_at)}</p>
                </div>
                <p className="text-gray-400 text-sm mt-2">{event.supporter_email || '—'}</p>
                <p className="text-gray-500 text-xs mt-2">{event.detail || '—'}</p>
              </div>
            ))}
          </div>
        ))}
      </Card>
    </div>
  );
};

export default SupportersTab;
